/**
 * Expand YOY cells into separate columns for current year, previous year, and delta
 * Used for time filter presets: 'current', 'previous', 'prev_and_current', 'current_and_next'
 *
 * Instead of having one column with a YOY object, creates three columns:
 * - "{Month} {CurrentYear}" (e.g., "November 2025")
 * - "{Month} {PreviousYear}" (e.g., "November 2024")
 * - "{Month} Δ" (delta column with delta symbol)
 */

import { BreakdownCell } from '@/components/common/breakdown-tables/types';
import { DimensionType } from '@/types/rebateAnalytics';

const DELTA_SYMBOL = 'Δ';

/**
 * Month order for sorting (Jan=0, Feb=1, ..., Dec=11)
 */
const MONTH_ORDER: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/**
 * Expand YOY cells into separate columns
 *
 * @param yoyCells - Cells with YOY objects { current, previous, delta }
 * @param currentYear - Current year (e.g., 2025)
 * @param previousYear - Previous year (e.g., 2024)
 * @param primaryXDimension - Primary X dimension to determine if TIME is on X-axis
 * @returns Expanded cells with separate columns for current, previous, and delta
 */
export function expandYOYToColumns(
  yoyCells: BreakdownCell[],
  currentYear: number,
  previousYear: number,
  primaryXDimension: DimensionType,
): BreakdownCell[] {
  const expandedCells: BreakdownCell[] = [];

  for (const cell of yoyCells) {
    if (
      !cell.value ||
      typeof cell.value !== 'object' ||
      !('current' in cell.value)
    ) {
      // Not a YOY cell, keep as-is
      expandedCells.push(cell);
      continue;
    }

    const yoyValue = cell.value as {
      current: number;
      previous: number;
      delta: number;
    };

    const timeColumn =
      primaryXDimension === DimensionType.TIME ? cell.column : cell.subColumn;

    if (!timeColumn) {
      // No time dimension found, keep as-is
      expandedCells.push(cell);
      continue;
    }

    // Handle both "Nov" and "November" formats
    const monthMatch = timeColumn.match(/^([A-Za-z]+)/);
    const monthName = monthMatch ? monthMatch[1] : timeColumn;

    const currentYearColumn = `${monthName} ${currentYear}`;
    const previousYearColumn = `${monthName} ${previousYear}`;
    const deltaColumn = `${monthName} ${DELTA_SYMBOL}`;

    if (primaryXDimension === DimensionType.TIME) {
      expandedCells.push(
        {
          ...cell,
          column: currentYearColumn,
          value: yoyValue.current,
        },
        {
          ...cell,
          column: previousYearColumn,
          value: yoyValue.previous,
        },
        {
          ...cell,
          column: deltaColumn,
          value: yoyValue.delta,
        },
      );
    } else {
      expandedCells.push(
        {
          ...cell,
          subColumn: currentYearColumn,
          value: yoyValue.current,
        },
        {
          ...cell,
          subColumn: previousYearColumn,
          value: yoyValue.previous,
        },
        {
          ...cell,
          subColumn: deltaColumn,
          value: yoyValue.delta,
        },
      );
    }
  }

  // Sort columns to ensure proper ordering: months chronologically, then current year, previous year, delta
  return expandedCells.sort((a, b) => {
    const aCol =
      primaryXDimension === DimensionType.TIME ? a.column : (a.subColumn ?? '');
    const bCol =
      primaryXDimension === DimensionType.TIME ? b.column : (b.subColumn ?? '');

    const aMatch = aCol.match(/^([A-Za-z]+)\s*(.+)?$/);
    const bMatch = bCol.match(/^([A-Za-z]+)\s*(.+)?$/);

    if (!aMatch || !bMatch) {
      return aCol.localeCompare(bCol);
    }

    const aMonth = aMatch[1];
    const bMonth = bMatch[1];
    const aSuffix = aMatch[2] || '';
    const bSuffix = bMatch[2] || '';

    // First sort by month (chronological order)
    const aMonthOrder = MONTH_ORDER[aMonth] ?? 999;
    const bMonthOrder = MONTH_ORDER[bMonth] ?? 999;
    const monthCompare = aMonthOrder - bMonthOrder;
    if (monthCompare !== 0) {
      return monthCompare;
    }

    // Then sort by suffix: current year first, then previous year, then delta
    const suffixOrder: Record<string, number> = {
      [`${currentYear}`]: 0,
      [`${previousYear}`]: 1,
      [DELTA_SYMBOL]: 2,
    };

    const aOrder = suffixOrder[aSuffix] ?? 999;
    const bOrder = suffixOrder[bSuffix] ?? 999;

    return aOrder - bOrder;
  });
}
